import { useState, useEffect } from 'react';
import { Mail, Download, Loader2, FileSpreadsheet } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { supabase } from '../lib/supabase';
import { exportPriceListToXlsx, formatCurrency } from '../lib/exportUtils';
import { useNotifications } from '../store/useStore';
import type { Database } from '../types/database.types';
import { SendPriceListEmailModal } from '../components/SendPriceListEmailModal';
import { generateEmailHTML } from '../lib/emailTemplate';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { PDFDocument, StandardFonts } from 'pdf-lib';
import { resolveNoVatClauseText } from '../lib/noVatClause';
import {
  A4_PAGE_WIDTH,
  A4_PAGE_HEIGHT,
  A4_MARGIN,
  COMPANY_FOOTER_DEFAULT,
  MARCHIO_PUBLIC_PATH,
  LOGOTIPO_PUBLIC_PATH,
  loadImageScaledDataUrl,
  drawPageFooter as drawPageFooterShared,
} from '../lib/letterheadPdf';
import { priceListLineTotalAmount, PRICE_LIST_CONSUMPTION_LABEL, isPriceOnConsumption } from '../lib/priceListItemPricing';
import { normalizePriceListItems } from '../lib/priceListCustomLines';
import { drawOfferConditionsAndSignatures } from '../lib/offerConditionsPdf';
import { drawOfferHeader, offerCustomerLines } from '../lib/offerHeaderPdf';
import {
  OFFER_CLAUSES,
  OFFER_REQUIREMENTS_TITLE,
  OFFER_SPECIFIC_APPROVAL_TEXT,
  OFFER_SPECIFIC_APPROVAL_TITLE,
  OFFER_TERMS_TITLE,
  shouldPrintOfferTerms,
} from '../lib/offerTerms';

type PriceList = Database['public']['Tables']['price_lists']['Row'];
type PriceListItem = Database['public']['Tables']['price_list_items']['Row'];

interface Customer {
  id: string;
  company_name: string;
  contact_person?: string;
  email?: string;
  address?: string;
  city?: string;
  postal_code?: string;
  province?: string;
  vat_number?: string;
  tax_code?: string;
  phone?: string;
}

interface Product {
  id: string;
  code: string;
  name: string;
  description?: string;
  category?: string;
  unit: string;
  base_price: number;
  photo_url?: string;
  photo_thumb_url?: string;
  cartone?: string;
  pallet?: string;
  ean?: string;
  scadenza?: string;
}

interface PriceListWithItems extends PriceList {
  customer?: Customer;
  price_list_items: (PriceListItem & {
    products: Product;
  })[];
}

// Riga del canone nel riepilogo: deve far capire che l'importo si paga ogni mese.
// L'importo sta in fondo: nei font standard del PDF il carattere dopo il «€» gli si accavalla.
const canoneSummaryLabel = (monthly: number, months: number): string =>
  `CANONE MENSILE (NEL TOTALE ${
    months === 1 ? 'È INCLUSO IL 1° MESE' : `SONO INCLUSI I PRIMI ${months} MESI`
  }) — OGNI MESE: ${formatCurrency(monthly)}`;

// Carica un PDF statico dal /public e ritorna i bytes
const fetchPdfBytes = async (url: string): Promise<ArrayBuffer> => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Impossibile caricare ${url}: ${res.status}`);
  return await res.arrayBuffer();
};

// Tronca il testo se eccede la larghezza disponibile (in pt) per il font dato
const fitText = (text: string, font: import('pdf-lib').PDFFont, size: number, maxWidth: number): string => {
  if (font.widthOfTextAtSize(text, size) <= maxWidth) return text;
  const ellipsis = '…';
  let lo = 0;
  let hi = text.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    const candidate = text.slice(0, mid) + ellipsis;
    if (font.widthOfTextAtSize(candidate, size) <= maxWidth) lo = mid;
    else hi = mid - 1;
  }
  return text.slice(0, lo) + ellipsis;
};

// Unisce all'offerta il Contratto (con celle "Dati del Cliente" compilate) e l'Informativa Privacy
const appendContractAndPrivacy = async (
  offerBytes: ArrayBuffer,
  customer: Customer | undefined
): Promise<Uint8Array> => {
  const merged = await PDFDocument.create();

  // 1. Pagine offerta
  const offerDoc = await PDFDocument.load(offerBytes);
  const offerPages = await merged.copyPages(offerDoc, offerDoc.getPageIndices());
  offerPages.forEach((p) => merged.addPage(p));

  // 2. Contratto: compila pag. 1, poi copia tutte le pagine
  const contractBytes = await fetchPdfBytes('/Contratto Abruzzo AI VENDITA.pdf');
  const contractDoc = await PDFDocument.load(contractBytes);
  const helvetica = await contractDoc.embedFont(StandardFonts.Helvetica);

  const ragioneSociale = customer?.company_name ?? '';
  const partitaIva = customer?.vat_number ?? '';
  const codiceFiscale = customer?.tax_code ?? '';
  const sedeLegale = [customer?.address, customer?.postal_code, customer?.city, customer?.province]
    .filter(Boolean)
    .join(' ');

  const page1 = contractDoc.getPage(0);
  const fontSize = 10;
  const cellX = 215;
  const cellMaxWidth = 380;
  const rows: Array<{ y: number; value: string }> = [
    { y: 565, value: ragioneSociale },
    { y: 545, value: partitaIva },
    { y: 525, value: codiceFiscale },
    { y: 505, value: sedeLegale },
  ];
  for (const row of rows) {
    if (!row.value) continue;
    const text = fitText(row.value, helvetica, fontSize, cellMaxWidth);
    page1.drawText(text, { x: cellX, y: row.y, size: fontSize, font: helvetica });
  }

  // Pag. 5: data di stampa nei 3 campi "Data:" (Fornitore, Cliente, Approvazione specifica)
  const page5 = contractDoc.getPage(4);
  const todayStr = new Date().toLocaleDateString('it-IT');
  const dateX = 437;
  const dateYs = [558, 468, 95];
  for (const y of dateYs) {
    page5.drawText(todayStr, { x: dateX, y, size: fontSize, font: helvetica });
  }

  const contractPages = await merged.copyPages(contractDoc, contractDoc.getPageIndices());
  contractPages.forEach((p) => merged.addPage(p));

  // 3. Informativa privacy as-is
  const privacyBytes = await fetchPdfBytes('/informativa privacy Contratto di Servizi.pdf');
  const privacyDoc = await PDFDocument.load(privacyBytes);
  const privacyPages = await merged.copyPages(privacyDoc, privacyDoc.getPageIndices());
  privacyPages.forEach((p) => merged.addPage(p));

  return await merged.save();
};

interface PriceListPrintViewProps {
  isOpen: boolean;
  onClose: () => void;
  priceListId: string;
  sortField?: 'code' | 'name';
  sortDirection?: 'asc' | 'desc';
  selectedCategory?: string;
  printByCategory?: boolean;
  canoneMonths?: number;
}

export function PriceListPrintView({
  isOpen,
  onClose,
  priceListId,
  sortField = 'name',
  sortDirection = 'asc',
  selectedCategory = 'all',
  printByCategory = false,
  canoneMonths: canoneMonthsProp,
}: PriceListPrintViewProps) {
  const [priceList, setPriceList] = useState<PriceListWithItems | null>(null);
  const [loading, setLoading] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [companyFooter, setCompanyFooter] = useState(COMPANY_FOOTER_DEFAULT);
  const [showFooterEdit, setShowFooterEdit] = useState(false);
  const { addNotification } = useNotifications();
  // printContentRef removed

  useEffect(() => {
    if (isOpen && priceListId) {
      loadPriceListData();
    }
  }, [isOpen, priceListId]);

  const loadPriceListData = async () => {
    try {
      setLoading(true);

      // Load price list with items
      const { data: priceListData, error: priceListError } = await supabase
        .from('price_lists')
        .select(`
          *,
          price_list_items (
            *,
            products (id, code, name, description, category, unit, base_price, photo_url, photo_thumb_url, cartone, pallet, ean, scadenza)
          )
        `)
        .eq('id', priceListId)
        .single();

      if (priceListError) {
        console.error('Error loading price list:', priceListError);
        throw priceListError;
      }
      if (!priceListData) throw new Error('Price list not found');

      // Load associated customer
      const { data: customerData } = await supabase
        .from('customers')
        .select('id, company_name, contact_person, email, address, city, postal_code, province, vat_number, tax_code, phone')
        .eq('id', (priceListData as any).customer_id)
        .maybeSingle();

      setPriceList({
        ...(priceListData as any),
        // Righe manuali (product_id null): prodotto sintetico dai campi custom_*
        price_list_items: normalizePriceListItems((priceListData as any).price_list_items),
        customer: customerData || undefined
      } as PriceListWithItems);

    } catch (error) {
      console.error('Error loading price list data:', error);
      addNotification({
        type: 'error',
        title: 'Errore',
        message: error instanceof Error ? error.message : 'Impossibile caricare l\'offerta'
      } as any);
    } finally {
      setLoading(false);
    }
  };

  // Genera PDF con carta intestata programmatica (Marchio + Logotipo + Footer)
  const generatePdfBlob = async (): Promise<Blob> => {
    if (!priceList) throw new Error('Dati dell\'offerta non disponibili');

    const showDiscount = (priceList as any).print_discount !== false;
    const colCount = showDiscount ? 6 : 4;
    const labelColSpan = showDiscount ? 5 : 3;

    try {
      const [marchioDataUrl, logotipoDataUrl] = await Promise.all([
        loadImageScaledDataUrl(MARCHIO_PUBLIC_PATH, 200),
        loadImageScaledDataUrl(LOGOTIPO_PUBLIC_PATH, 440),
      ]);

      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const pageWidth = A4_PAGE_WIDTH;
      const pageHeight = A4_PAGE_HEIGHT;
      const margin = A4_MARGIN;

      const yPos = drawOfferHeader(
        doc,
        {
          marchioDataUrl,
          logotipoDataUrl,
          customer: priceList.customer,
          offerName: priceList.name,
          dateLabel: `Data: ${new Date(priceList.created_at).toLocaleDateString('it-IT')}`,
        },
        { pageWidth, margin }
      );

      // --- TABELLA PRODOTTI ---
      let tableRows: any[] = [];
      let currentCategory = '';

      const items = [...priceList.price_list_items].filter(item => {
        if (selectedCategory === 'all') return true;
        return item.products.category === selectedCategory;
      });

      items.sort((a, b) => {
        if (printByCategory) {
          const catA = a.products.category || '';
          const catB = b.products.category || '';
          const catCompare = catA.localeCompare(catB);
          if (catCompare !== 0) return catCompare;
        }
        if (sortField === 'code') {
          const codeA = a.products.code || '';
          const codeB = b.products.code || '';
          const compare = codeA.localeCompare(codeB);
          return sortDirection === 'asc' ? compare : -compare;
        } else {
          const nameA = a.products.name || '';
          const nameB = b.products.name || '';
          const compare = nameA.localeCompare(nameB);
          return sortDirection === 'asc' ? compare : -compare;
        }
      });

      items.forEach(item => {
        if (printByCategory) {
          const category = item.products.category || 'Senza categoria';
          if (category !== currentCategory) {
            tableRows.push([{
              content: category,
              colSpan: colCount,
              styles: { fillColor: [219, 234, 254], fontStyle: 'bold', textColor: [30, 58, 138] }
            }]);
            currentCategory = category;
          }
        }
        const finalPrice = item.price * (1 - (item.discount_percentage || 0) / 100);
        const onCons = isPriceOnConsumption(item as any);
        const row: any[] = [
          item.products.code || '',
          item.products.name || '',
          `${item.min_quantity} ${item.products.unit}`,
        ];
        if (showDiscount) {
          row.push(
            onCons ? PRICE_LIST_CONSUMPTION_LABEL : formatCurrency(item.price),
            onCons ? '—' : `${item.discount_percentage || 0}%`
          );
        }
        row.push(onCons ? PRICE_LIST_CONSUMPTION_LABEL : formatCurrency(finalPrice * (item.min_quantity || 1)));
        tableRows.push(row);
      });

      // Calcolo Totali
      const canoneItemsPdf = items.filter(i => (i.products.category || '').toLowerCase() === 'canone');
      const prodottiItemsPdf = items.filter(i => (i.products.category || '').toLowerCase() !== 'canone');
      const hasCanonePdf = canoneItemsPdf.length > 0;
      const months = canoneMonthsProp ?? (priceList as any).canone_months ?? 6;

      const calcTotalPdf = (list: typeof items) =>
        list.reduce((acc, item) => acc + priceListLineTotalAmount(item as any), 0);

      const noVatForfettario = (priceList as any).no_vat_forfettario === true;
      const noVatClauseResolved = noVatForfettario
        ? resolveNoVatClauseText((priceList as any).no_vat_clause_text)
        : '';
      const hasConsumptionPdf = items.some((i) => isPriceOnConsumption(i as any));

      let footRows: any[] = [];
      
      if (!hasCanonePdf) {
        const totalAmount = calcTotalPdf(items);
        if (noVatForfettario) {
          footRows = [
            [
              { content: 'TOTALE OFFERTA', colSpan: labelColSpan, styles: { halign: 'right', fontStyle: 'bold', fontSize: 10, fillColor: [243, 244, 246], textColor: [17, 24, 39] } },
              { content: formatCurrency(totalAmount), styles: { halign: 'right', fontStyle: 'bold', fontSize: 10, fillColor: [255, 255, 255], textColor: [15, 23, 42] } }
            ]
          ];
        } else {
          const vatAmount = totalAmount * 0.22;
          const totalWithVat = totalAmount + vatAmount;

          footRows = [
            [
              { content: 'PRODOTTI — IMPONIBILE', colSpan: labelColSpan, styles: { halign: 'right', fontStyle: 'bold', fillColor: [243, 244, 246], textColor: [107, 114, 128] } },
              { content: formatCurrency(totalAmount), styles: { halign: 'right', fontStyle: 'bold', fillColor: [255, 255, 255], textColor: [55, 65, 81] } }
            ],
            [
              { content: 'IVA (22%)', colSpan: labelColSpan, styles: { halign: 'right', fillColor: [243, 244, 246], textColor: [107, 114, 128] } },
              { content: formatCurrency(vatAmount), styles: { halign: 'right', fillColor: [255, 255, 255], textColor: [55, 65, 81] } }
            ],
            [
              { content: 'TOTALE OFFERTA', colSpan: labelColSpan, styles: { halign: 'right', fontStyle: 'bold', fontSize: 10, fillColor: [243, 244, 246], textColor: [17, 24, 39] } },
              { content: formatCurrency(totalWithVat), styles: { halign: 'right', fontStyle: 'bold', fontSize: 10, fillColor: [255, 255, 255], textColor: [15, 23, 42] } }
            ]
          ];
        }
      } else {
        const prodottiTotal = calcTotalPdf(prodottiItemsPdf);
        const canoneMonthly = calcTotalPdf(canoneItemsPdf);
        const canonePeriod = canoneMonthly * months;

        if (noVatForfettario) {
          const grandTotal = prodottiTotal + canonePeriod;
          footRows = [
            [
              { content: 'TOT. PRODOTTI', colSpan: labelColSpan, styles: { halign: 'right', fontStyle: 'bold', fillColor: [249, 250, 251], textColor: [17, 24, 39] } },
              { content: formatCurrency(prodottiTotal), styles: { halign: 'right', fontStyle: 'bold', fillColor: [255, 255, 255], textColor: [15, 23, 42] } }
            ],
            [
              { content: canoneSummaryLabel(canoneMonthly, months), colSpan: labelColSpan, styles: { halign: 'right', fontStyle: 'bold', fillColor: [255, 251, 235], textColor: [180, 83, 9] } },
              { content: formatCurrency(canonePeriod), styles: { halign: 'right', fontStyle: 'bold', fillColor: [255, 251, 235], textColor: [146, 64, 14] } }
            ],
            [
              { content: 'TOTALE OFFERTA', colSpan: labelColSpan, styles: { halign: 'right', fontStyle: 'bold', fontSize: 11, fillColor: [243, 244, 246], textColor: [17, 24, 39] } },
              { content: formatCurrency(grandTotal), styles: { halign: 'right', fontStyle: 'bold', fontSize: 11, fillColor: [255, 255, 255], textColor: [15, 23, 42] } }
            ]
          ];
        } else {
          const prodottiVat = prodottiTotal * 0.22;
          const prodottiWithVat = prodottiTotal + prodottiVat;

          const canoneVat = canonePeriod * 0.22;
          const canoneWithVat = canonePeriod + canoneVat;

          const grandTotal = prodottiWithVat + canoneWithVat;

          footRows = [
            [
              { content: 'PRODOTTI — IMPONIBILE', colSpan: labelColSpan, styles: { halign: 'right', fontStyle: 'bold', fillColor: [249, 250, 251], textColor: [107, 114, 128] } },
              { content: formatCurrency(prodottiTotal), styles: { halign: 'right', fontStyle: 'bold', fillColor: [255, 255, 255], textColor: [55, 65, 81] } }
            ],
            [
              { content: 'IVA (22%)', colSpan: labelColSpan, styles: { halign: 'right', fillColor: [249, 250, 251], textColor: [107, 114, 128] } },
              { content: formatCurrency(prodottiVat), styles: { halign: 'right', fillColor: [255, 255, 255], textColor: [55, 65, 81] } }
            ],
            [
              { content: 'TOT. PRODOTTI', colSpan: labelColSpan, styles: { halign: 'right', fontStyle: 'bold', fillColor: [249, 250, 251], textColor: [17, 24, 39] } },
              { content: formatCurrency(prodottiWithVat), styles: { halign: 'right', fontStyle: 'bold', fillColor: [255, 255, 255], textColor: [15, 23, 42] } }
            ],
            [
              { content: canoneSummaryLabel(canoneMonthly, months), colSpan: labelColSpan, styles: { halign: 'right', fontStyle: 'bold', fillColor: [255, 251, 235], textColor: [180, 83, 9] } },
              { content: formatCurrency(canonePeriod), styles: { halign: 'right', fontStyle: 'bold', fillColor: [255, 251, 235], textColor: [146, 64, 14] } }
            ],
            [
              { content: 'IVA (22%)', colSpan: labelColSpan, styles: { halign: 'right', fillColor: [255, 251, 235], textColor: [217, 119, 6] } },
              { content: formatCurrency(canoneVat), styles: { halign: 'right', fillColor: [255, 251, 235], textColor: [146, 64, 14] } }
            ],
            [
              { content: 'TOT. CANONE', colSpan: labelColSpan, styles: { halign: 'right', fontStyle: 'bold', fillColor: [255, 251, 235], textColor: [120, 53, 15] } },
              { content: formatCurrency(canoneWithVat), styles: { halign: 'right', fontStyle: 'bold', fillColor: [255, 251, 235], textColor: [120, 53, 15] } }
            ],
            [
              { content: 'TOTALE OFFERTA', colSpan: labelColSpan, styles: { halign: 'right', fontStyle: 'bold', fontSize: 11, fillColor: [243, 244, 246], textColor: [17, 24, 39] } },
              { content: formatCurrency(grandTotal), styles: { halign: 'right', fontStyle: 'bold', fontSize: 11, fillColor: [255, 255, 255], textColor: [15, 23, 42] } }
            ]
          ];
        }
      }

      const drawPageFooter = (pageDoc: jsPDF, _pageNum: number) => {
        drawPageFooterShared(pageDoc, companyFooter, pageWidth, pageHeight, margin);
      };

      const drawContinuationLabel = () => {
        doc.setFontSize(8);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(120, 120, 120);
        doc.text(`${priceList.name} - segue`, margin, 10);
      };

      autoTable(doc, {
        startY: yPos,
        head: [showDiscount
          ? ['Codice', 'Prodotto', 'Qta', 'Listino', 'Sconto', 'Prezzo']
          : ['Codice', 'Prodotto', 'Qta', 'Prezzo']],
        body: tableRows,
        foot: footRows,
        margin: { left: margin, right: margin, bottom: 40 },
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 1.6, lineColor: [229, 231, 235], lineWidth: 0.1 },
        headStyles: { fillColor: [0, 162, 154], textColor: [255, 255, 255], fontStyle: 'bold' },
        columnStyles: showDiscount
          ? {
              0: { cellWidth: 25 },
              1: { cellWidth: 'auto' },
              2: { cellWidth: 15, halign: 'center' },
              3: { cellWidth: 20, halign: 'right' },
              4: { cellWidth: 15, halign: 'center', textColor: [21, 128, 61], fontStyle: 'bold' },
              5: { cellWidth: 25, halign: 'right', fontStyle: 'bold', textColor: [15, 23, 42] }
            }
          : {
              0: { cellWidth: 25 },
              1: { cellWidth: 'auto' },
              2: { cellWidth: 15, halign: 'center' },
              3: { cellWidth: 25, halign: 'right', fontStyle: 'bold', textColor: [15, 23, 42] }
            },
        alternateRowStyles: { fillColor: [249, 250, 251] },
        didDrawPage: (data: any) => {
          if (data.pageNumber > 1) drawContinuationLabel();
          drawPageFooter(doc, data.pageNumber);
        }
      });

      let currentY = (doc as any).lastAutoTable.finalY + 6;

      if (noVatForfettario && noVatClauseResolved) {
        if (currentY > pageHeight - 50) {
          doc.addPage();
          currentY = 20;
          drawPageFooter(doc, doc.getNumberOfPages());
        }
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(75, 85, 99);
        const clauseLines = doc.splitTextToSize(noVatClauseResolved, pageWidth - 2 * margin);
        doc.text(clauseLines, margin, currentY);
        currentY += clauseLines.length * 3.8 + 5;
      }

      if (hasConsumptionPdf) {
        if (currentY > pageHeight - 45) {
          doc.addPage();
          currentY = 20;
          drawPageFooter(doc, doc.getNumberOfPages());
        }
        doc.setFontSize(7);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(75, 85, 99);
        const consLines = doc.splitTextToSize(
          `I prodotti in categoria «${PRICE_LIST_CONSUMPTION_LABEL}» non sono inclusi nel totale (importo variabile in base al traffico).`,
          pageWidth - 2 * margin
        );
        doc.text(consLines, margin, currentY);
        currentY += consLines.length * 3.8 + 8;
      }

      // --- CONDIZIONI CONTRATTUALI E FIRME ---
      drawOfferConditionsAndSignatures(doc, currentY, {
        printConditions: shouldPrintOfferTerms(priceList.print_conditions),
        values: {
          payment: priceList.payment_conditions,
          delivery: priceList.delivery_conditions,
          notes: priceList.notes,
        },
        frame: {
          pageWidth,
          pageHeight,
          margin,
          onNewPage: () => {
            drawContinuationLabel();
            drawPageFooter(doc, doc.getNumberOfPages());
          },
        },
      });

      const offerBytes = doc.output('arraybuffer');
      const includeAttachments = (priceList as any).print_attachments !== false;
      if (!includeAttachments) {
        return new Blob([offerBytes], { type: 'application/pdf' });
      }
      const mergedBytes = await appendContractAndPrivacy(offerBytes, priceList.customer);
      return new Blob([mergedBytes], { type: 'application/pdf' });

    } catch (error) {
      console.error('Error generating PDF:', error);
      const msg = error instanceof Error ? error.message : 'Errore sconosciuto';
      addNotification({ type: 'error', title: 'Errore PDF', message: msg });
      throw new Error(`Errore PDF: ${msg}`);
    }
  };

  // Apre la modale email
  const handleSendEmail = () => {
    if (!priceList) {
      addNotification({
        type: 'warning',
        title: 'Offerta non disponibile',
        message: 'Impossibile aprire la modale di invio email'
      } as any);
      return;
    }
    setIsEmailModalOpen(true);
  };

  // Gestisce l'invio completo dell'email (genera PDF, carica in bucket, chiama webhook)
  const handleSendEmailComplete = async (emailData: { email: string; subject: string; body: string }) => {
    if (!priceList) {
      throw new Error('Offerta non disponibile');
    }

    try {
      setIsGeneratingPDF(true);

      // Genera PDF Blob client-side
      // Genera PDF Blob client-side (include già il template)
      const pdfBlob = await generatePdfBlob();

      if (!pdfBlob || pdfBlob.size === 0) {
        throw new Error('Il PDF generato è vuoto.');
      }

      // Carica PDF su Supabase Storage
      const priceListId = priceList.id;
      const timestamp = Date.now();
      const fileName = `offerta_${priceListId}_${timestamp}.pdf`;

      const { error: uploadError } = await supabase.storage
        .from('order-pdfs')
        .upload(fileName, pdfBlob, {
          contentType: 'application/pdf',
          upsert: false
        });

      if (uploadError) {
        throw new Error(`Errore nel caricamento del PDF: ${uploadError.message}`);
      }

      // Ottieni URL pubblico
      const { data: urlData } = supabase.storage
        .from('order-pdfs')
        .getPublicUrl(fileName);

      const pdfUrl = urlData?.publicUrl;
      if (!pdfUrl) {
        throw new Error('Impossibile ottenere l\'URL pubblico del PDF');
      }

      // Chiama webhook N8N
      const webhookUrl = import.meta.env.VITE_N8N_PRICELIST_WEBHOOK_URL;
      if (!webhookUrl) {
        throw new Error('URL webhook N8N non configurato. Configurare VITE_N8N_PRICELIST_WEBHOOK_URL');
      }

      // Genera HTML formattato per l'email
      const htmlBody = generateEmailHTML(
        emailData.body,
        priceList.customer?.company_name || 'Cliente',
        priceList.name
      );

      const webhookPayload = {
        email: emailData.email,
        subject: emailData.subject,
        body: emailData.body, // Testo semplice per client email che non supportano HTML (fallback)
        htmlBody: htmlBody, // HTML formattato con colori AbruzzoAI - USA QUESTO per l'email!
        html: htmlBody, // Alias per maggiore chiarezza - contiene l'HTML completo formattato
        emailHtml: htmlBody, // Altro alias - contenuto HTML dell'email
        pdfUrl: pdfUrl,
        priceListId: priceList.id,
        priceListName: priceList.name,
        customerName: priceList.customer?.company_name || 'Cliente',
        customerEmail: priceList.customer?.email || emailData.email,
        fileName: fileName,
        // Flag per indicare che l'HTML è disponibile
        hasHtml: true,
        emailFormat: 'html'
      };

      const webhookResponse = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(webhookPayload)
      });

      if (!webhookResponse.ok) {
        const errorText = await webhookResponse.text();
        throw new Error(`Errore nella chiamata al webhook N8N: ${errorText}`);
      }

      // Solo una notifica finale di successo
      addNotification({
        type: 'success',
        title: 'Email inviata',
        message: `Email inviata con successo a: ${emailData.email}`
      } as any);

    } catch (error) {
      console.error('Error sending email:', error);
      throw error; // Rilancia l'errore per essere gestito dalla modale
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!priceList) {
      console.error('handleDownloadPDF: priceList is null');
      return;
    }

    // Previeni click multipli
    if (isGeneratingPDF) {
      return;
    }

    setIsGeneratingPDF(true);

    try {
      // Genera PDF Blob con template già incluso
      const pdfBlob = await generatePdfBlob();

      const pdfUrl = URL.createObjectURL(pdfBlob);

      // Download PDF with proper filename
      const today = new Date().toLocaleDateString('it-IT').replace(/\//g, '-');
      const customerName = priceList.customer?.company_name || 'Cliente';
      const fileName = `offerta_${customerName.replace(/[^a-zA-Z0-9]/g, '_')}_${today}.pdf`;
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = fileName;
      link.style.display = 'none';
      document.body.appendChild(link);

      // Usa requestAnimationFrame per assicurarsi che il DOM sia aggiornato
      requestAnimationFrame(() => {
        link.click();

        // Clean up after download
        setTimeout(() => {
          document.body.removeChild(link);
          URL.revokeObjectURL(pdfUrl);
        }, 2000);
      });

      addNotification({
        type: 'success',
        title: 'PDF Generato',
        message: 'L\'offerta è stata scaricata come PDF'
      } as any);

    } catch (error) {
      console.error('Error generating PDF:', error);
      const errorMessage = error instanceof Error ? error.message : 'Errore nella generazione del PDF';
      addNotification({
        type: 'error',
        title: 'Errore',
        message: errorMessage
      } as any);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleDownloadExcel = async () => {
    if (!priceList) {
      addNotification({
        type: 'warning',
        title: 'Offerta non disponibile',
        message: 'Impossibile esportare l\'offerta in Excel'
      } as any);
      return;
    }

    // Previeni click multipli
    if (isGeneratingExcel || isGeneratingPDF) return;

    setIsGeneratingExcel(true);
    try {
      const today = new Date().toLocaleDateString('it-IT').replace(/\//g, '-');
      const customerName = priceList.customer?.company_name || 'Cliente';
      const fileName = `offerta_${customerName.replace(/[^a-zA-Z0-9]/g, '_')}_${today}`;

      const ok = await exportPriceListToXlsx({
        priceList,
        filename: fileName,
        sortField,
        sortDirection,
        selectedCategory,
        printByCategory,
      });

      if (!ok) {
        throw new Error('Esportazione Excel fallita');
      }

      addNotification({
        type: 'success',
        title: 'Excel Generato',
        message: 'L\'offerta è stata scaricata come Excel'
      } as any);
    } catch (error) {
      console.error('Error exporting Excel:', error);
      const errorMessage = error instanceof Error ? error.message : 'Errore nella generazione dell\'Excel';
      addNotification({
        type: 'error',
        title: 'Errore',
        message: errorMessage
      } as any);
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  const calculateFinalPrice = (basePrice: number, discount: number) => {
    return basePrice * (1 - discount / 100);
  };

  // Image loading no longer needed - handled by backend Puppeteer



  if (!isOpen) return null;

  const showDiscount = (priceList as any)?.print_discount !== false;
  const colCount = showDiscount ? 6 : 4;
  const labelColSpan = showDiscount ? 5 : 3;

  return (
    <>
      {/* Print Styles */}
      <style>{`
        @media print {
          /* Reset all visibility */
          * {
            visibility: visible !important;
          }
          
          /* Hide modal overlay and close button during print */
          [data-radix-dialog-overlay],
          [data-radix-dialog-close] {
            visibility: hidden !important;
            display: none !important;
          }
          
          /* Hide non-print elements */
          .no-print {
            display: none !important;
          }
          
          /* Ensure print content is visible */
          .print-content,
          .print-page,
          .print-visible {
            visibility: visible !important;
            display: block !important;
          }
          
          /* Page setup - A4 Portrait */
          .print-page {
            width: 210mm;
            min-height: 297mm;
            margin: 0;
            padding: 45mm 20mm 30mm 20mm; /* Added top/bottom padding for letterhead */
            /* La carta intestata e' disegnata nel markup (marchio + logotipo
               + linea teal), non come immagine di sfondo: il vecchio
               carta_intestata_abruzzo_white.jpg non esiste piu' in public/. */
            background: white;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            image-rendering: -webkit-optimize-contrast;
            box-shadow: none;
            page-break-after: always;
          }
          
          /* Print-specific styles */
          .print-header {
            border-bottom: 2px solid #2563eb;
            padding-bottom: 20px;
            margin-bottom: 30px;
          }
          
          .print-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 20px;
          }
          
          .print-table th,
          .print-table td {
            border: 1px solid #e5e7eb;
            padding: 6px 8px; /* righe più sottili in stampa */
            text-align: left;
            font-size: 12px;
            vertical-align: top;
          }
          
          .print-table th {
            background-color: #2563eb;
            color: white;
            font-weight: 600;
          }
          
          .print-table tbody tr:nth-child(even) {
            background-color: #f9fafb;
          }
        }
        
        @page {
          size: A4 portrait;
          margin: 0;
        }
      `}</style>

      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="print-modal-container max-w-7xl h-[calc(100dvh-1rem)] sm:h-auto max-h-[calc(100dvh-1rem)] sm:max-h-[95vh] overflow-auto p-0 z-[90]" overlayClassName="z-[90]">
          <DialogHeader className="no-print p-4 sm:p-6 pb-0 sm:pb-0">
            <DialogTitle>Anteprima Offerta</DialogTitle>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : priceList ? (
            <div className="print-content">
              <div
                className="print-page bg-white mx-auto print-visible relative"
                style={{
                  width: '210mm',
                  minHeight: '297mm',
                  padding: '10mm 20mm 25mm 20mm',
                }}
              >
                {/* Testata: logo a sinistra e marchio a destra, come nel PDF e nelle guide all'offerta */}
                <div className="flex items-center justify-between pb-3 border-b-2" style={{ borderColor: '#00a29a' }}>
                  <img src={LOGOTIPO_PUBLIC_PATH} alt="AbruzzoAI" className="object-contain" style={{ width: '180px', height: 'auto' }} />
                  <img src={MARCHIO_PUBLIC_PATH} alt="" className="w-14 h-14 object-contain" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  <div>
                    <h2 className="text-3xl font-bold" style={{ color: '#0b4f53' }}>Offerta</h2>
                    <p className="font-bold" style={{ color: '#00a29a' }}>{priceList.name}</p>
                    <p className="text-sm text-gray-500">Data: {new Date(priceList.created_at).toLocaleDateString('it-IT')}</p>
                  </div>
                  <div className="p-3 border-l-4" style={{ backgroundColor: '#e8f5f4', borderColor: '#00a29a' }}>
                    <p className="text-[10px] font-bold" style={{ color: '#00a29a' }}>SPETT.LE</p>
                    <p className="font-bold" style={{ color: '#0b4f53' }}>{priceList.customer?.company_name}</p>
                    <div className="text-xs text-gray-700">
                      {offerCustomerLines(priceList.customer).map((line) => <p key={line}>{line}</p>)}
                    </div>
                  </div>
                </div>

                {/* Products Table */}
                <table className="print-table w-full border-collapse mt-4">
                  <thead>
                    <tr className="text-white border-b" style={{ backgroundColor: '#00a29a' }}>
                      <th className="px-2 py-2 text-left w-24">Codice</th>
                      <th className="px-2 py-2 text-left">Prodotto</th>
                      <th className="px-2 py-2 text-center w-16">Qta</th>
                      {showDiscount && (
                        <>
                          <th className="px-2 py-2 text-right w-24">Listino</th>
                          <th className="px-2 py-2 text-center w-16">Sconto</th>
                        </>
                      )}
                      <th className="px-2 py-2 text-right w-24">Prezzo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      // Filtra i prodotti
                      const filteredItems = [...priceList.price_list_items].filter(item => {
                        if (selectedCategory === 'all') return true;
                        return item.products.category === selectedCategory;
                      });

                      // Se printByCategory è true, raggruppa per categoria
                      if (printByCategory) {
                        // Raggruppa per categoria
                        const groupedByCategory = filteredItems.reduce((acc, item) => {
                          const category = item.products.category || 'Senza categoria';
                          if (!acc[category]) {
                            acc[category] = [];
                          }
                          acc[category].push(item);
                          return acc;
                        }, {} as Record<string, typeof filteredItems>);

                        // Ordina le categorie alfabeticamente
                        const sortedCategories = Object.keys(groupedByCategory).sort();

                        // Genera le righe raggruppate per categoria
                        let globalIndex = 0;
                        const palette = [
                          { bg: 'bg-blue-100', text: 'text-blue-900', border: 'border-blue-200' },
                          { bg: 'bg-green-100', text: 'text-green-900', border: 'border-green-200' },
                          { bg: 'bg-yellow-100', text: 'text-yellow-900', border: 'border-yellow-200' },
                          { bg: 'bg-purple-100', text: 'text-purple-900', border: 'border-purple-200' },
                          { bg: 'bg-pink-100', text: 'text-pink-900', border: 'border-pink-200' },
                          { bg: 'bg-indigo-100', text: 'text-indigo-900', border: 'border-indigo-200' },
                          { bg: 'bg-teal-100', text: 'text-teal-900', border: 'border-teal-200' },
                          { bg: 'bg-orange-100', text: 'text-orange-900', border: 'border-orange-200' },
                        ];
                        return sortedCategories.flatMap(category => {
                          const categoryItems = groupedByCategory[category]
                            .sort((a, b) => {
                              let comparison = 0;
                              if (sortField === 'code') {
                                comparison = (a.products.code || '').localeCompare(b.products.code || '');
                              } else {
                                comparison = (a.products.name || '').localeCompare(b.products.name || '');
                              }
                              return sortDirection === 'asc' ? comparison : -comparison;
                            });

                          const color = palette[sortedCategories.indexOf(category) % palette.length];
                          return [
                            // Intestazione categoria
                            <tr key={`category-${category}`} className={`${color.bg}`}>
                              <td colSpan={colCount} className={`border ${color.border} px-2 py-0.5 font-semibold text-xs ${color.text}`}>
                                {category}
                              </td>
                            </tr>,
                            // Prodotti della categoria
                            ...categoryItems.map((item) => {
                              const finalPrice = calculateFinalPrice(item.price, item.discount_percentage);
                              const onCons = isPriceOnConsumption(item as any);
                              const rowIndex = globalIndex++;

                              return (
                                <tr key={item.id} className={rowIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>

                                  <td className="border border-gray-300 px-2 py-2 font-mono text-xs align-top">
                                    {item.products.code}
                                  </td>
                                  <td className="border border-gray-300 px-2 py-2 align-top">
                                    <div>
                                      <div className="font-medium text-xs">{item.products.name}</div>
                                      {item.products.description && (
                                        <div className="text-xs text-gray-500 mt-1">
                                          {item.products.description}
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                  <td className="border border-gray-300 px-2 py-2 text-center text-xs align-top">
                                    {item.min_quantity} {item.products.unit}
                                  </td>
                                  {showDiscount && (
                                    <>
                                      <td className="border border-gray-300 px-2 py-2 text-right text-xs align-top text-gray-500">
                                        {onCons ? PRICE_LIST_CONSUMPTION_LABEL : formatCurrency(item.price)}
                                      </td>
                                      <td className="border border-gray-300 px-2 py-2 text-center text-xs align-top font-semibold text-green-700">
                                        {onCons ? '—' : `${item.discount_percentage || 0}%`}
                                      </td>
                                    </>
                                  )}
                                  <td className="border border-gray-300 px-2 py-2 text-right font-bold text-slate-900 text-xs align-top">
                                    {onCons ? PRICE_LIST_CONSUMPTION_LABEL : formatCurrency(finalPrice * (item.min_quantity || 1))}
                                  </td>
                                </tr>
                              );
                            })
                          ];
                        });
                      } else {
                        return filteredItems
                          .sort((a, b) => {
                            let comparison = 0;
                            if (sortField === 'code') {
                              comparison = (a.products.code || '').localeCompare(b.products.code || '');
                            } else {
                              comparison = (a.products.name || '').localeCompare(b.products.name || '');
                            }
                            return sortDirection === 'asc' ? comparison : -comparison;
                          })
                          .map((item, index) => {
                            const finalPrice = calculateFinalPrice(item.price, item.discount_percentage);
                            const onCons = isPriceOnConsumption(item as any);

                            return (
                              <tr key={item.id} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>

                                <td className="border border-gray-300 px-2 py-2 font-mono text-xs align-top">
                                  {item.products.code}
                                </td>
                                <td className="border border-gray-300 px-2 py-2 align-top">
                                  <div>
                                    <div className="font-medium text-xs">{item.products.name}</div>
                                    {item.products.description && (
                                      <div className="text-xs text-gray-500 mt-1">
                                        {item.products.description}
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td className="border border-gray-300 px-2 py-2 text-center text-xs align-top">
                                  {item.min_quantity} {item.products.unit}
                                </td>
                                {showDiscount && (
                                  <>
                                    <td className="border border-gray-300 px-2 py-2 text-right text-xs align-top text-gray-500">
                                      {onCons ? PRICE_LIST_CONSUMPTION_LABEL : formatCurrency(item.price)}
                                    </td>
                                    <td className="border border-gray-300 px-2 py-2 text-center text-xs align-top font-semibold text-green-700">
                                      {onCons ? '—' : `${item.discount_percentage || 0}%`}
                                    </td>
                                  </>
                                )}
                                <td className="border border-gray-300 px-2 py-2 text-right font-bold text-slate-900 text-xs align-top">
                                  {onCons ? PRICE_LIST_CONSUMPTION_LABEL : formatCurrency(finalPrice * (item.min_quantity || 1))}
                                </td>
                              </tr>
                            );
                          });
                      }
                    })()}
                  </tbody>
                  <tfoot className="bg-gray-100 border-t-2 border-gray-300 font-bold">
                    {(() => {
                      const items = priceList.price_list_items;
                      const canoneItems = items.filter(i => (i.products.category || '').toLowerCase() === 'canone');
                      const prodottiItems = items.filter(i => (i.products.category || '').toLowerCase() !== 'canone');
                      const hasCanone = canoneItems.length > 0;
                      const months = canoneMonthsProp ?? (priceList as any).canone_months ?? 6;
                      const noVatForfettario = (priceList as any).no_vat_forfettario === true;
                      const noVatClauseResolved = noVatForfettario
                        ? resolveNoVatClauseText((priceList as any).no_vat_clause_text)
                        : '';

                      const calcTotal = (list: typeof items) =>
                        list.reduce((acc, item) => acc + priceListLineTotalAmount(item as any), 0);

                      const hasConsumptionPreview = items.some((i) => isPriceOnConsumption(i as any));

                      if (!hasCanone) {
                        const totalAmount = calcTotal(items);
                        if (noVatForfettario) {
                          return (
                            <>
                              <tr>
                                <td colSpan={labelColSpan} className="px-2 py-3 text-right text-gray-900 font-bold text-base">TOTALE</td>
                                <td className="px-2 py-3 text-right text-slate-900 border border-gray-300 font-bold text-base">{formatCurrency(totalAmount)}</td>
                              </tr>
                              <tr className="bg-white font-normal">
                                <td colSpan={colCount} className="px-2 py-2 text-left text-[10px] text-gray-600 italic leading-snug border border-gray-200">
                                  {noVatClauseResolved}
                                </td>
                              </tr>
                              {hasConsumptionPreview && (
                                <tr className="bg-white font-normal">
                                  <td colSpan={colCount} className="px-2 py-1 text-left text-[10px] text-gray-500 italic border border-gray-200">
                                    I prodotti in categoria «{PRICE_LIST_CONSUMPTION_LABEL}» non sono inclusi nel totale (importo variabile in base al traffico).
                                  </td>
                                </tr>
                              )}
                            </>
                          );
                        }
                        const vatAmount = totalAmount * 0.22;
                        const totalWithVat = totalAmount + vatAmount;
                        return (
                          <>
                            <tr>
                              <td colSpan={labelColSpan} className="px-2 py-2 text-right text-gray-700 font-medium">Imponibile</td>
                              <td className="px-2 py-2 text-right text-gray-700 border border-gray-300 text-sm">{formatCurrency(totalAmount)}</td>
                            </tr>
                            <tr>
                              <td colSpan={labelColSpan} className="px-2 py-2 text-right text-gray-700 font-medium">IVA (22%)</td>
                              <td className="px-2 py-2 text-right text-gray-700 border border-gray-300 text-sm">{formatCurrency(vatAmount)}</td>
                            </tr>
                            <tr>
                              <td colSpan={labelColSpan} className="px-2 py-3 text-right text-gray-900 font-bold text-base">TOTALE</td>
                              <td className="px-2 py-3 text-right text-slate-900 border border-gray-300 font-bold text-base">{formatCurrency(totalWithVat)}</td>
                            </tr>
                            {hasConsumptionPreview && (
                              <tr className="bg-white font-normal">
                                <td colSpan={colCount} className="px-2 py-1 text-left text-[10px] text-gray-500 italic border border-gray-200">
                                  I prodotti in categoria «{PRICE_LIST_CONSUMPTION_LABEL}» non sono inclusi nel totale (importo variabile in base al traffico).
                                </td>
                              </tr>
                            )}
                          </>
                        );
                      }

                      const prodottiTotal = calcTotal(prodottiItems);
                      const canoneMonthly = calcTotal(canoneItems);
                      const canonePeriod = canoneMonthly * months;

                      if (noVatForfettario) {
                        const grandTotal = prodottiTotal + canonePeriod;
                        return (
                          <>
                            <tr>
                              <td colSpan={labelColSpan} className="px-2 py-2 text-right text-gray-900 font-bold text-sm">TOT. PRODOTTI</td>
                              <td className="px-2 py-2 text-right text-slate-900 border border-gray-300 font-bold text-sm">{formatCurrency(prodottiTotal)}</td>
                            </tr>
                            <tr className="bg-amber-50">
                              <td colSpan={labelColSpan} className="px-2 py-1 text-right text-amber-700 font-semibold text-xs uppercase tracking-wide">
                                {canoneSummaryLabel(canoneMonthly, months)}
                              </td>
                              <td className="px-2 py-1 text-right text-amber-800 border border-gray-300 text-sm">{formatCurrency(canonePeriod)}</td>
                            </tr>
                            <tr className="border-t-2 border-gray-400">
                              <td colSpan={labelColSpan} className="px-2 py-3 text-right text-gray-900 font-bold text-base">TOTALE OFFERTA</td>
                              <td className="px-2 py-3 text-right text-slate-900 border border-gray-300 font-bold text-base">{formatCurrency(grandTotal)}</td>
                            </tr>
                            <tr className="bg-white font-normal">
                              <td colSpan={colCount} className="px-2 py-2 text-left text-[10px] text-gray-600 italic leading-snug border border-gray-200">
                                {noVatClauseResolved}
                              </td>
                            </tr>
                            {hasConsumptionPreview && (
                              <tr className="bg-white font-normal">
                                <td colSpan={colCount} className="px-2 py-1 text-left text-[10px] text-gray-500 italic border border-gray-200">
                                  I prodotti in categoria «{PRICE_LIST_CONSUMPTION_LABEL}» non sono inclusi nel totale (importo variabile in base al traffico).
                                </td>
                              </tr>
                            )}
                          </>
                        );
                      }

                      const prodottiVat = prodottiTotal * 0.22;
                      const prodottiWithVat = prodottiTotal + prodottiVat;

                      const canoneVat = canonePeriod * 0.22;
                      const canoneWithVat = canonePeriod + canoneVat;

                      const grandTotal = prodottiWithVat + canoneWithVat;

                      return (
                        <>
                          <tr className="bg-gray-50">
                            <td colSpan={labelColSpan} className="px-2 py-1 text-right text-gray-500 font-semibold text-xs uppercase tracking-wide">Prodotti — Imponibile</td>
                            <td className="px-2 py-1 text-right text-gray-700 border border-gray-300 text-sm">{formatCurrency(prodottiTotal)}</td>
                          </tr>
                          <tr className="bg-gray-50">
                            <td colSpan={labelColSpan} className="px-2 py-1 text-right text-gray-500 font-medium text-xs">IVA (22%)</td>
                            <td className="px-2 py-1 text-right text-gray-700 border border-gray-300 text-sm">{formatCurrency(prodottiVat)}</td>
                          </tr>
                          <tr>
                            <td colSpan={labelColSpan} className="px-2 py-2 text-right text-gray-900 font-bold text-sm">TOT. PRODOTTI</td>
                            <td className="px-2 py-2 text-right text-slate-900 border border-gray-300 font-bold text-sm">{formatCurrency(prodottiWithVat)}</td>
                          </tr>
                          <tr className="bg-amber-50">
                            <td colSpan={labelColSpan} className="px-2 py-1 text-right text-amber-700 font-semibold text-xs uppercase tracking-wide">
                              {canoneSummaryLabel(canoneMonthly, months)}
                            </td>
                            <td className="px-2 py-1 text-right text-amber-800 border border-gray-300 text-sm">{formatCurrency(canonePeriod)}</td>
                          </tr>
                          <tr className="bg-amber-50">
                            <td colSpan={labelColSpan} className="px-2 py-1 text-right text-amber-600 font-medium text-xs">IVA (22%)</td>
                            <td className="px-2 py-1 text-right text-amber-800 border border-gray-300 text-sm">{formatCurrency(canoneVat)}</td>
                          </tr>
                          <tr>
                            <td colSpan={labelColSpan} className="px-2 py-2 text-right text-amber-900 font-bold text-sm">TOT. CANONE</td>
                            <td className="px-2 py-2 text-right text-amber-900 border border-gray-300 font-bold text-sm">{formatCurrency(canoneWithVat)}</td>
                          </tr>
                          <tr className="border-t-2 border-gray-400">
                            <td colSpan={labelColSpan} className="px-2 py-3 text-right text-gray-900 font-bold text-base">TOTALE OFFERTA</td>
                            <td className="px-2 py-3 text-right text-slate-900 border border-gray-300 font-bold text-base">{formatCurrency(grandTotal)}</td>
                          </tr>
                          {hasConsumptionPreview && (
                            <tr className="bg-white font-normal">
                              <td colSpan={colCount} className="px-2 py-1 text-left text-[10px] text-gray-500 italic border border-gray-200">
                                I prodotti in categoria «{PRICE_LIST_CONSUMPTION_LABEL}» non sono inclusi nel totale (importo variabile in base al traffico).
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })()}
                  </tfoot>
                </table>

                {/* Condizioni contrattuali */}
                {shouldPrintOfferTerms(priceList.print_conditions) && (
                  <div className="mt-3 p-3 border-l-4" style={{ backgroundColor: '#e8f5f4', borderColor: '#00a29a' }}>
                    <h3 className="text-xs font-bold mb-1" style={{ color: '#0b4f53' }}>{OFFER_TERMS_TITLE}</h3>
                    <div className="flex items-start gap-x-4 text-xs">
                      <div className="flex-1 flex flex-col gap-y-1">
                        {priceList.payment_conditions && (
                          <div>
                            <span className="font-bold text-gray-600">Pagamento:</span>
                            <span className="ml-1">{priceList.payment_conditions}</span>
                          </div>
                        )}
                        {priceList.shipping_conditions && (
                          <div>
                            <span className="font-bold text-gray-600">Trasporto:</span>
                            <span className="ml-1">{priceList.shipping_conditions}</span>
                          </div>
                        )}
                        {priceList.delivery_conditions && (
                          <div>
                            <span className="font-bold text-gray-600">Durata del contratto:</span>
                            <span className="ml-1">{priceList.delivery_conditions}</span>
                          </div>
                        )}
                      </div>
                      {priceList.notes && (
                        <div className="flex-1 pl-4">
                          <span className="font-bold text-gray-600">Note:</span>
                          <span className="ml-1 whitespace-pre-wrap">{priceList.notes}</span>
                        </div>
                      )}
                    </div>
                    <div className="mt-2 pt-2 border-t border-teal-200 sm:columns-2 gap-4 space-y-1.5 text-[11px] leading-snug text-gray-700">
                      {OFFER_CLAUSES.map((clause) => (
                        <div key={clause.id}>
                          <p className="font-bold">{clause.title}</p>
                          <p>{clause.text}</p>
                          {clause.items && (
                            <>
                              <p className="font-bold mt-1">{OFFER_REQUIREMENTS_TITLE}</p>
                              <ol className="list-decimal pl-4">
                                {clause.items.map((item) => <li key={item}>{item}</li>)}
                              </ol>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {shouldPrintOfferTerms(priceList.print_conditions) && (
                  <div className="mt-2 text-[11px] leading-snug text-gray-700">
                    <p className="font-bold">{OFFER_SPECIFIC_APPROVAL_TITLE}</p>
                    <p>{OFFER_SPECIFIC_APPROVAL_TEXT}</p>
                  </div>
                )}

                {/* Date della licenza: non vanno confuse con la validità dell'offerta (Clausola A) */}
                <div className="mt-4 text-right text-xs text-gray-500">
                  {priceList.valid_from && (
                    <p>Licenza dal {new Date(priceList.valid_from).toLocaleDateString('it-IT')}</p>
                  )}
                  {priceList.valid_until && (
                    <p>fino al {new Date(priceList.valid_until).toLocaleDateString('it-IT')}</p>
                  )}
                </div>

                {/* Footer Intestazione Azienda */}
                <div className="mt-auto pt-4">
                  <div className="border-t border-gray-300 pt-2 text-center">
                    <p className="text-xs text-gray-500">{companyFooter}</p>
                  </div>
                </div>

              </div>
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500 no-print">
              Errore nel caricamento del listino
            </div>
          )}

          {/* Footer with action buttons */}
          <div className="no-print p-4 sm:p-6 pt-4 sm:pt-0 border-t bg-gray-50 sticky bottom-0">
            {/* Footer editabile */}
            <div className="mb-3">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <label className="text-xs font-medium text-gray-600">Piè di pagina (intestazione azienda):</label>
                <button
                  type="button"
                  onClick={() => setShowFooterEdit(!showFooterEdit)}
                  className="text-xs text-blue-600 hover:text-blue-800 underline"
                >
                  {showFooterEdit ? 'Nascondi' : 'Modifica'}
                </button>
              </div>
              {showFooterEdit && (
                <input
                  type="text"
                  value={companyFooter}
                  onChange={(e) => setCompanyFooter(e.target.value)}
                  className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  placeholder="es. Ragione Sociale S.r.l. — P.IVA: 01234567890 — Via Roma 1, 00100 Roma (RM)"
                />
              )}
            </div>
            <div className="grid grid-cols-1 gap-2 sm:flex sm:items-center sm:justify-end sm:gap-0 sm:space-x-3">
              <Button
                onClick={handleSendEmail}
                variant="outline"
                className="flex items-center justify-center gap-2 w-full sm:w-auto"
                disabled={!priceList?.customer?.email || isGeneratingPDF || isGeneratingExcel}
              >
                {isGeneratingPDF || isGeneratingExcel ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Generazione in corso...</span>
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4" />
                    <span>Invia Email</span>
                  </>
                )}
              </Button>
              <Button
                onClick={handleDownloadExcel}
                variant="outline"
                className="flex items-center justify-center gap-2 w-full sm:w-auto"
                disabled={isGeneratingPDF || isGeneratingExcel}
              >
                {isGeneratingExcel ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Generazione in corso...</span>
                  </>
                ) : (
                  <>
                    <FileSpreadsheet className="w-4 h-4" />
                    <span>Download EXCEL</span>
                  </>
                )}
              </Button>
              <Button
                onClick={handleDownloadPDF}
                className="flex items-center justify-center gap-2 w-full sm:w-auto"
                disabled={isGeneratingPDF || isGeneratingExcel}
              >
                {isGeneratingPDF ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Generazione in corso...</span>
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    <span>Download PDF</span>
                  </>
                )}
              </Button>
            </div>
            {!priceList?.customer?.email && (
              <p className="text-xs text-gray-500 mt-2 text-right">
                Email non disponibile per questo cliente
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog >

      {/* Modale di loading durante generazione PDF */}
      {
        isGeneratingPDF && (
          <Dialog open={isGeneratingPDF} onOpenChange={() => { }}>
            <DialogContent className="max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
              <DialogHeader>
                <DialogTitle>Generazione PDF in corso</DialogTitle>
              </DialogHeader>
              <div className="flex flex-col items-center justify-center py-8">
                <Loader2 className="w-12 h-12 animate-spin text-primary mb-4" />
                <p className="text-center text-gray-600">
                  Stiamo generando il PDF del listino...
                  <br />
                  <span className="text-sm text-gray-500 mt-2 block">
                    Attendere il completamento del download
                  </span>
                </p>
              </div>
            </DialogContent>
          </Dialog>
        )
      }

      {/* Modale invio email */}
      {
        priceList && (
          <SendPriceListEmailModal
            isOpen={isEmailModalOpen}
            onClose={() => setIsEmailModalOpen(false)}
            priceListId={priceList.id}
            priceListName={priceList.name}
            customerEmail={priceList.customer?.email}
            customerName={priceList.customer?.company_name}
            onSend={handleSendEmailComplete}
          />
        )
      }
    </>
  );
}
export default PriceListPrintView;